import {
  BadgeCheck,
  BarChart3,
  Blocks,
  BookOpen,
  Bot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  EyeOff,
  FlaskConical,
  Globe,
  Headset,
  Image as ImageIcon,
  Inbox,
  Layers,
  Lightbulb,
  ListChecks,
  Lock,
  LogOut,
  Megaphone,
  Menu as MenuIcon,
  MessageCircle,
  Mic,
  Moon,
  Music,
  Network,
  Pencil,
  Route,
  ShieldCheck,
  Sun,
  Target,
  User,
  UserCircle,
  UserPlus,
  Users,
  Video,
  Zap,
} from "lucide-react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Portal,
  Snackbar,
  styled,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import axios from "axios";
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { identify } from "../capture";
import LoadingScreen from "../components/LoadingScreen";
import NotificationDropdown from "../components/NotificationDropdown";
import ParallelDialog from "../components/ParallelDialog";
import UserImpersonationDialog from "../components/UserImpersonationDialog";
import { DEMO_MODE_KEY, HIDE_LOGO_KEY, PERSONAL_ASSISTANT_NAME } from "../constants";
import DemoSettingsDialog from "../components/DemoSettingsDialog";
import { FEATURES } from "../constants/features";
import LucideIcon from "../components/LucideIcon";
import { SUBSCRIPTION_TYPES } from "../constants/subscriptionTypes";
import { AuthUser, useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeatureContext";
import { NotificationProvider } from "../context/NotificationContext";
import {
  TutorialProvider,
  useTutorial,
  TUTORIAL_CUTOFF_DATE,
  TUTORIAL_PAGES,
  TutorialPageKey,
} from "../context/TutorialContext";
import TutorialWrapper from "../components/TutorialWrapper";
import { useTutorialPage } from "../hooks/useTutorialPage";
import {
  profileTutorialSteps,
  companiesTutorialSteps,
} from "../tutorials/tutorialSteps";
import { Step } from "react-joyride";
import { useWhitelabel } from "../context/WhitelabelContext";
import { EventObj } from "../types";
import {
  getInstanceConfig,
  getLogoMode,
  getSupportContactInfo,
  getUserScopedKey,
  hostname,
  parseError,
  toAvatarName,
} from "../utils";
import { CompanyUser } from "../utils/roleUtils";
import { isFreeUser } from "../utils/subscriptionUtils";
import { useEmployeeTerminology } from "../utils/terminology";
import AdminPage from "./admin/AdminPage";
import Companies from "./companies/Companies";
import Company from "./companies/Company";
import Documentation from "./documentation/Documentation";
import OnboardingProgressMenuItem from "./onboarding/guided/OnboardingProgressMenuItem";
import Profile from "./profile/Profile";
import Support from "./support/Support";
import SupportTicketDetail from "./support/SupportTicketDetail";
import TicketFormDialog from "./support/dialogs/TicketFormDialog";
import WhiteLabel from "./whitelabel/Whitelabel";
import SubscriptionDialog from "./profile/SubscriptionDialog";
import PersonalAssistant from "../components/PersonalAssistant";
import ExecutiveAssistantTutorialDialog from "../components/PersonalAssistant/ExecutiveAssistantTutorialDialog";

const eventsReducer = (state: EventObj, action: any): EventObj => {
  switch (action.type) {
    case "SET_ERROR":
      return { ...state, error: action.error, success: "", severity: "error" };
    case "SET_INFO":
      return { ...state, error: action.info, success: "", severity: "info" };
    case "SET_SUCCESS":
      return {
        ...state,
        error: "",
        success: action.info || action.message,
        severity: "success",
      };
    default:
      throw new Error();
  }
};

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})<{
  open?: boolean;
  isMobile: boolean;
}>(({ theme, open, isMobile }) => ({
  width: "100vw",
  marginLeft: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: isMobile ? "100vw" : `calc(100vw - ${drawerWidth}px)`,
    marginLeft: isMobile ? 0 : drawerWidth,
  }),
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100vw - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }: { theme: any }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const Dashboard = ({
  setThemeMode,
  mode,
}: {
  setThemeMode: Function;
  mode: string;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Initialize drawer state - will be set properly once we have user ID
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const {
    currentUser: authUser,
    getToken: getAuthToken,
    signOut,
    signIn,
    loading: authLoading,
  } = useAuth();
  const [currentUser, setCurrentUser] = useState<any | AuthUser>({});
  const [token, setToken] = useState<any | string>("");
  const { screenName, subScreenName, companyId, instanceId } =
    useParams();
  const [event, eventListener] = useReducer(eventsReducer, {
    error: "",
    success: "",
    severity: "error",
  });
  const {
    whitelabelSettings,
    whitelabelLogos,
    loadWhitelabelByDomain,
    loaded,
  } = useWhitelabel();
  const { setFeatures, isFeatureEnabled, isFeatureLocked, isFeatureHidden } =
    useFeatures();
  const [init, setInit] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(SUBSCRIPTION_TYPES.FREE);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [userCurrentPlan, setUserCurentPlan] = useState(
    SUBSCRIPTION_TYPES.FREE
  );
  const [companies, setCompanies] = useState<Array<any>>([]);
  const [employees, setEmployees] = useState<Array<any>>([]);
  const [activeCompanyId, setActiveCompanyId] = useState(companyId);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectCompanyOpen, setSelectCompanyOpen] = useState(false);
  const [parallelDialogOpen, setParallelDialogOpen] = useState(false);
  const [parallelDialogTitle, setParallelDialogTitle] = useState("");
  const [parallelDialogContent, setParallelDialogContent] = useState("");
  const [parallelDialogButtonText, setParallelDialogButtonText] = useState("");
  const [company, setCompany] = useState<any | null>(null);
  const [usage, setUsage] = useState<any>({});
  const [toolsOpen, setToolsOpen] = React.useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [hasWhitelabelAccess, setHasWhitelabelAccess] = useState(false);
  const [isOrgAccount, setIsOrgAccount] = useState(false);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [companyRole, setCompanyRole] = useState<string | null | undefined>(
    undefined
  );
  // Unified menu apps from /usage: whitelabel-configured apps + builder apps
  // the user has explicitly added to their menu. The backend merges and
  // verifies access; we only render.
  const [menuApps, setMenuApps] = useState<
    Array<{
      id: string;
      name: string;
      icon: string;
      url?: string;
      appId?: string;
      public?: boolean;
      enabled?: boolean;
    }>
  >([]);
  const [parallelDialogOnClick, setParallelDialogOnClick] = useState<
    (() => void) | undefined
  >();
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);
  // Add state for search query at the component level
  const [searchQuery, setSearchQuery] = useState("");
  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [impersonationDialogOpen, setImpersonationDialogOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [demoMode, setDemoMode] = useState(() => {
    return localStorage.getItem(DEMO_MODE_KEY) === "true";
  });
  const [hideLogo, setHideLogo] = useState(() => {
    return localStorage.getItem(HIDE_LOGO_KEY) === "true";
  });
  const [demoSettingsDialogOpen, setDemoSettingsDialogOpen] = useState(false);
  const [personalAssistantOpen, setPersonalAssistantOpen] = useState(false);
  const [personalAssistantPrefs, setPersonalAssistantPrefs] = useState<{
    personaName?: string | null;
    personaImageUrl?: string | null;
  } | null>(null);

  // Get custom terminology
  const { employees: employeesText } = useEmployeeTerminology();

  // Filter companies based on search query (add this above the return statement)
  const filteredCompanies = companies.filter((company) =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // The currently selected company, when its guided onboarding was started (has completed
  // steps) but not finished (no completedAt). When present, we offer a "Resume Onboarding"
  // entry in the menu so the user can pick the flow back up. We scope this to the active
  // company only so the menu item doesn't show just because one of the user's other
  // companies has an unfinished onboarding. The guided flow now runs on the main platform
  // and whitelabels alike, so this is no longer gated on isMainSite.
  const activeOnboardingCompany = companies.find(
    (c) => c.id === activeCompanyId
  );
  const resumableOnboardingCompany =
    activeOnboardingCompany &&
    Array.isArray(activeOnboardingCompany.onboardingCompletedSteps) &&
    activeOnboardingCompany.onboardingCompletedSteps.length > 0 &&
    !activeOnboardingCompany.onboardingCompletedAt
      ? activeOnboardingCompany
      : undefined;

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const handleClick = () => {
    setToolsOpen(!toolsOpen);
  };

  const handleClose = (screenName: string) => {
    setDrawerOpen(false);
    navigate("/dashboard/" + screenName);
  };

  const changeScreen = (screenName: string) => {
    navigate("/dashboard/" + screenName);
  };

  // Try to load whitelabel settings by domain when component first mounts
  useEffect(() => {
    if (!loaded) {
      loadWhitelabelByDomain();
    }
  }, [loaded, loadWhitelabelByDomain]);

  useEffect(() => {
    // Wait for auth to finish loading before checking user state
    if (authLoading) {
      return;
    }

    if (authUser) {
      identify(authUser);
      setCurrentUser(authUser);

      // Check if this is the user's first visit
      const hasVisitedKey = `hasVisited_${authUser.uid}`;
      const hasVisited = localStorage.getItem(hasVisitedKey);

      if (!hasVisited && !isMobile) {
        // First visit on desktop - open the drawer
        setDrawerOpen(true);
        // Mark that the user has visited
        localStorage.setItem(hasVisitedKey, "true");
      }
    } else {
      // Not logged in, redirect to login
      navigate("/login");
    }
  }, [authUser, authLoading, isMobile, navigate]);

  const getCompany = async () => {
    // Ensure we have a valid user before attempting to get company
    if (!currentUser || !currentUser.uid) {
      setPlanLoaded(true);
      return;
    }

    try {
      const currentToken = await getToken();

      // If we still don't have a token, don't make the request
      if (!currentToken) {
        setPlanLoaded(true);
        return;
      }

      const res = await axios.get(`${hostname}/companies/${activeCompanyId}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      setCompany(res.data.company);
      if (res.data.company.subscriptionType) {
        setCurrentPlan(res.data.company.subscriptionType);
      }
      setPlanLoaded(true);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      setPlanLoaded(true);
    }
  };

  // Update activeCompanyId when URL companyId param changes (e.g., from navigate())
  useEffect(() => {
    if (companyId && companyId !== activeCompanyId) {
      setActiveCompanyId(companyId);
      localStorage.setItem(getUserScopedKey(currentUser.uid), companyId);
    }
  }, [companyId]);

  useEffect(() => {
    if (activeCompanyId && currentUser && currentUser.uid && activeCompanyId) {
      setPlanLoaded(false);
      getCompany();
      getEmployees();
    }
  }, [activeCompanyId, currentUser]);

  const getCurrentPlan = async () => {
    // Ensure we have a valid user before attempting to get plan
    if (!currentUser || !currentUser.uid) {
      return;
    }

    try {
      const currentToken = await getToken();

      // If we still don't have a token, don't make the request
      if (!currentToken) {
        return;
      }

      // Build URL with companyId parameter if activeCompanyId is set
      const url = activeCompanyId
        ? `${hostname}/usage?companyId=${activeCompanyId}`
        : `${hostname}/usage`;

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      setUsage(res.data.usage);
      setMenuApps(Array.isArray(res.data.apps) ? res.data.apps : []);
      if (res.data.usage.subscriptionType) {
        setUserCurentPlan(res.data.usage.subscriptionType);
      }
      // Set super user status from usage data
      setIsSuperUser(res.data.usage.superUser || false);
      // Set whitelabel access status (owner or in owners list)
      setHasWhitelabelAccess(res.data.hasWhitelabelAccess || false);
      setIsOrgAccount(!!res.data.usage.isOrgManaged);
      setUsageLoaded(true);
      // Set company role from usage response
      setCompanyRole(res.data.companyRole || null);
      // Set feature access from backend
      if (res.data.features) {
        console.log(
          "Features for company:",
          activeCompanyId,
          res.data.features
        );
        setFeatures(res.data.features);
      }

    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      setUsageLoaded(true);
    }
  };

  const getCompanies = async () => {
    // Ensure we have a valid user before attempting to get companies
    if (!currentUser || !currentUser.uid) {
      return;
    }

    try {
      const currentToken = await getToken();

      // If we still don't have a token, don't make the request
      if (!currentToken) {
        return;
      }

      const res = await axios.get(`${hostname}/companies`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      // If user has no companies, redirect to onboarding
      if (res.data.companies && res.data.companies.length === 0) {
        navigate('/onboarding');
        return;
      }

      setCompanies(res.data.companies);
      verifySetActiveCompany(res.data.companies);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const getEmployees = async () => {
    // Ensure we have a valid user before attempting to get employees
    if (!currentUser || !currentUser.uid) {
      return;
    }

    try {
      const currentToken = await getToken();

      // If we still don't have a token, don't make the request
      if (!currentToken) {
        return;
      }

      const res = await axios.get(
        `${hostname}/companies/${activeCompanyId}/employees`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      setEmployees(res.data.employees);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  // Refresh employees when navigating to employees or chats tab
  useEffect(() => {
    if (
      activeCompanyId &&
      currentUser?.uid &&
      screenName === "companies" &&
      (subScreenName === "employees" || subScreenName === "chats")
    ) {
      getEmployees();
    }
  }, [subScreenName]);

  const verifySetActiveCompany = (companies: Array<any>) => {
    if (!activeCompanyId && companies && companies.length > 0) {
      const currentCompany = companies[0];
      setActiveCompanyId(currentCompany.id);
    }
  };

  const getActiveCompanyName = () => {
    const activeCompany = companies.find((x) => x.id === activeCompanyId);
    return activeCompany ? activeCompany.name : "";
  };

  const getActiveCompanyLogoUrl = () => {
    const activeCompany = companies.find((x) => x.id === activeCompanyId);
    return activeCompany ? activeCompany.logoUrl : "";
  };

  const getDashboardLogo = () => {
    // Use whitelabel horizontal logo if available, otherwise use default logo
    // Use dark mode logo if in dark mode and available
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  // Stable identity so descendants that include `getToken` in dep arrays
  // (e.g. BuilderAppRuntime's API client / file fetch, AppRuntime's iframe
  // bundle) don't refire and remount on every Dashboard re-render — for
  // example when the side menu drawer toggles.
  const getToken = useCallback(async () => {
    return getAuthToken();
  }, [getAuthToken]);

  const signOutAction = () => {
    signOut();
    window.location.replace("/login");
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate("/dashboard/profile");
  };

  const handleLogoutClick = () => {
    handleProfileMenuClose();
    signOutAction();
  };

  const handleImpersonateClick = () => {
    handleProfileMenuClose();
    setImpersonationDialogOpen(true);
  };

  const handleDemoSettingsClick = () => {
    handleProfileMenuClose();
    setDemoSettingsDialogOpen(true);
  };

  const handleImpersonate = async (
    customToken: string,
    superUserToken: string
  ) => {
    try {
      // Store the super user's custom token, email, and active company before impersonating
      const currentEmail = currentUser.email;
      const currentUid = currentUser.uid;

      localStorage.setItem("impersonation_original_token", superUserToken);
      localStorage.setItem("impersonation_original_email", currentEmail || "");
      localStorage.setItem("impersonation_original_uid", currentUid || "");

      // Store the super user's active company ID if they have one
      if (activeCompanyId) {
        localStorage.setItem(
          "impersonation_original_company_id",
          activeCompanyId
        );
      }

      localStorage.setItem("is_impersonating", "true");

      // Sign in with the target user's custom token using AuthContext
      signIn(customToken);

      // Redirect to companies page to avoid trying to access super user's company
      window.location.href = "/dashboard/companies";
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: "Failed to impersonate user",
      });
    }
  };

  const handleStopImpersonating = async () => {
    try {
      // Get the original super user's data
      const originalToken = localStorage.getItem(
        "impersonation_original_token"
      );
      const originalUid = localStorage.getItem("impersonation_original_uid");
      const originalCompanyId = localStorage.getItem(
        "impersonation_original_company_id"
      );

      if (!originalToken) {
        eventListener({
          type: "SET_ERROR",
          error: "No original session found",
        });
        return;
      }

      // Clear the impersonated user's stored active company
      // This prevents trying to access their company after switching back
      if (currentUser && currentUser.uid) {
        localStorage.removeItem(getUserScopedKey(currentUser.uid));
      }

      // Restore the super user's active company if they had one
      if (originalUid && originalCompanyId) {
        localStorage.setItem(getUserScopedKey(originalUid), originalCompanyId);
      }

      // Clear impersonation data
      localStorage.removeItem("impersonation_original_token");
      localStorage.removeItem("impersonation_original_email");
      localStorage.removeItem("impersonation_original_uid");
      localStorage.removeItem("impersonation_original_company_id");
      localStorage.removeItem("is_impersonating");

      // Sign in with the original super user's token using AuthContext
      signIn(originalToken);

      // Redirect to companies page if they had a company, otherwise just to /dashboard/companies
      if (originalCompanyId) {
        window.location.href = `/dashboard/companies/${originalCompanyId}`;
      } else {
        window.location.href = "/dashboard/companies";
      }
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: "Failed to stop impersonating",
      });
    }
  };

  const handleCloseError = () => {
    eventListener({ type: "SET_ERROR", error: "" });
    eventListener({ type: "SET_SUCCESS", message: "" });
  };

  const openDialog = (
    title: string,
    content: string,
    buttonText: string,
    onClick: () => void
  ) => {
    setParallelDialogOpen(true);
    setParallelDialogTitle(title);
    setParallelDialogContent(content);
    setParallelDialogButtonText(buttonText);
    // Wrap in arrow function to prevent React from calling it as a functional update
    setParallelDialogOnClick(() => onClick);
  };

  const restoreActiveCompany = (userId: string) => {
    if (!userId || activeCompanyId) return; // Exit if no user or ID exists
    const storedId = localStorage.getItem(getUserScopedKey(userId));
    if (storedId) setActiveCompanyId(storedId);
  };

  const updateActiveCompany = (companyId: string, userId: string) => {
    setActiveCompanyId(companyId);
    localStorage.setItem(getUserScopedKey(userId), companyId);
  };

  const saveActiveCompanyId = (companyId: string) => {
    updateActiveCompany(companyId, currentUser.uid);
    if (screenName === "companies" && !subScreenName) {
      navigate(`/dashboard/companies`);
    } else if (screenName === "whitelabel") {
      navigate(`/dashboard/whitelabel`);
    } else if (subScreenName) {
      navigate(`/dashboard/${screenName}/${companyId}/${subScreenName}`);
    } else {
      navigate(`/dashboard/${screenName}/${companyId}/${screenName}`);
    }
  };

  const parallelDialogOnClickAction = () => {
    if (parallelDialogOnClick) {
      parallelDialogOnClick();
    }
    setParallelDialogOpen(false);
  };

  const parallelDialogOnClose = () => {
    setParallelDialogOpen(false);
  };

  useEffect(() => {
    if (currentUser.uid && !init) {
      restoreActiveCompany(currentUser.uid);
      setInit(true);
    }
    if (currentUser.uid && init) {
      getCompanies();
      getCurrentPlan();
    }
  }, [activeCompanyId, currentUser, init]);

  // Check if we're currently impersonating
  useEffect(() => {
    const impersonating = localStorage.getItem("is_impersonating") === "true";
    setIsImpersonating(impersonating);
  }, []);

  const dashboardReady =
    !authLoading && init && (!activeCompanyId || (usageLoaded && planLoaded));

  if (!dashboardReady) {
    return <LoadingScreen open={true} />;
  }

  // Determine if user is "new" (created after the tutorial cutoff date)
  // New users will see tutorials on first visit to each page
  const isNewUser = (): boolean => {
    if (!usage?.created_date) return false;
    const userCreatedDate = new Date(usage.created_date);
    return userCreatedDate >= TUTORIAL_CUTOFF_DATE;
  };

  return (
    <TutorialProvider getToken={getToken}>
      <DashboardTutorialInitializer
        usage={usage}
        isNewUser={isNewUser()}
        getToken={getToken}
      />
      <ExecutiveAssistantTutorialInitializer usageLoaded={usageLoaded} />
      <NotificationProvider companyId={activeCompanyId} getToken={getToken}>
        <Box>
        <ParallelDialog
          open={parallelDialogOpen}
          title={parallelDialogTitle}
          content={parallelDialogContent}
          buttonText={parallelDialogButtonText}
          onClick={parallelDialogOnClickAction}
          onClose={parallelDialogOnClose}
        />
        <Portal>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={event.error !== "" || event.success !== ""}
            autoHideDuration={6000}
            onClose={handleCloseError}
          >
            <Alert onClose={handleCloseError} severity={event.severity}>
              {event.error || event.success}
            </Alert>
          </Snackbar>
        </Portal>
        {/* iOS safe area background - prevents content showing behind navbar in standalone mode */}
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "env(safe-area-inset-top, 0px)",
            backgroundColor: instanceConfig.menuColor,
            zIndex: theme.zIndex.appBar + 1,
          }}
        />
        <AppBar
          position="fixed"
          elevation={0}
          open={drawerOpen}
          theme={theme}
          sx={{
            backgroundColor: instanceConfig.menuColor,
            borderBottom:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.12)"
                : "1px solid rgba(0, 0, 0, 0.12)",
            top: "env(safe-area-inset-top, 0px)",
          }}
        >
          <Toolbar>
            <IconButton
              onClick={toggleDrawer(!drawerOpen)}
              edge="start"
              color="inherit"
              size="large"
              sx={{
                ...(drawerOpen && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
            {!hideLogo && (
              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  justifyItems: "center",
                }}
              >
                <img
                  src={getDashboardLogo()}
                  alt="logo"
                  style={{
                    objectFit: "contain",
                    height: "100%",
                    width: "100%",
                    maxWidth: 200,
                    maxHeight: 35,
                  }}
                />
              </Box>
            )}
            <Typography style={{ flexGrow: 1 }} />
            {isFreeUser(currentPlan) &&
              !isOrgAccount &&
              usageLoaded &&
              window.innerWidth > 570 &&
              activeCompanyId &&
              planLoaded &&
              (whitelabelSettings?.customSubscriptionEnabled ? (
                <Button
                  onClick={() => {
                    const salesEmail =
                      whitelabelSettings?.salesEmail ||
                      getSupportContactInfo(whitelabelSettings);
                    console.log(
                      "[Dashboard] Contact Sales clicked - whitelabelSettings.salesEmail:",
                      whitelabelSettings?.salesEmail
                    );
                    console.log(
                      "[Dashboard] Contact Sales clicked - final salesEmail:",
                      salesEmail
                    );
                    window.open(
                      `mailto:${salesEmail}?subject=Subscription Inquiry`,
                      "_blank"
                    );
                  }}
                  variant="outlined"
                >
                  <Zap size={20} style={{ marginRight: 5 }} />
                  Contact Sales
                </Button>
              ) : (
                <Button
                  onClick={() => setSubscriptionDialogOpen(true)}
                  variant="outlined"
                >
                  <Zap size={20} style={{ marginRight: 5 }} />
                  Upgrade
                </Button>
              ))}
            {!isMobile && (
              <IconButton
                sx={{ ml: 1 }}
                onClick={() => {
                  // If whitelabel has custom support URL, open it in new tab
                  if (whitelabelSettings?.supportUrl) {
                    const supportUrl =
                      getSupportContactInfo(whitelabelSettings);
                    window.open(supportUrl, "_blank");
                  } else {
                    // Otherwise open ticket creation dialog
                    setTicketDialogOpen(true);
                  }
                }}
                title="Contact Support"
                color="inherit"
              >
                <Headset />
              </IconButton>
            )}
            <NotificationDropdown
              companyId={activeCompanyId}
              getToken={getToken}
            />
            {!isMobile && (
              <IconButton
                sx={{ ml: 1 }}
                onClick={() =>
                  setThemeMode(mode === "light" ? "dark" : "light")
                }
                color="inherit"
              >
                {mode === "dark" ? <Sun /> : <Moon />}
              </IconButton>
            )}
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ ml: 1 }}
              aria-label="account menu"
              aria-controls="profile-menu"
              aria-haspopup="true"
            >
              <Avatar
                alt={usage.displayName || currentUser.displayName}
                src={usage.photo || ""}
              />
            </IconButton>
            <Menu
              id="profile-menu"
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon>
                  <UserCircle size={20} />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>
              {isSuperUser && !isImpersonating && (
                <MenuItem onClick={handleImpersonateClick}>
                  <ListItemIcon>
                    <User size={20} />
                  </ListItemIcon>
                  <ListItemText>Impersonate User</ListItemText>
                </MenuItem>
              )}
              {(isSuperUser || hasWhitelabelAccess) && !isImpersonating && (
                <MenuItem onClick={handleDemoSettingsClick}>
                  <ListItemIcon>
                    <EyeOff size={20} />
                  </ListItemIcon>
                  <ListItemText>Demo Settings</ListItemText>
                </MenuItem>
              )}
              {isImpersonating && (
                <MenuItem onClick={handleStopImpersonating}>
                  <ListItemIcon>
                    <User size={20} />
                  </ListItemIcon>
                  <ListItemText>Stop Impersonating</ListItemText>
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleLogoutClick}>
                <ListItemIcon>
                  <LogOut size={20} />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            backgroundColor: instanceConfig.menuColor,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: isMobile
                ? theme.palette.background.default
                : instanceConfig.menuColor,
              top: "env(safe-area-inset-top, 0px)",
              height: "calc(100% - env(safe-area-inset-top, 0px))",
            },
          }}
          variant={isMobile ? "temporary" : "persistent"}
          anchor="left"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
        >
          <DrawerHeader theme={theme}>
            <IconButton onClick={() => setDrawerOpen(false)}>
              {theme.direction === "ltr" ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={(e: any) => {
                  setAnchorEl(e.currentTarget);
                  setSelectCompanyOpen(true);
                }}
              >
                <ListItemAvatar>
                  <Avatar src={getActiveCompanyLogoUrl()}>
                    {toAvatarName(getActiveCompanyName())}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={getActiveCompanyName()}
                  sx={{
                    "& .MuiListItemText-primary": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                <ChevronsUpDown style={{ flexShrink: 0 }} />
              </ListItemButton>
              <Menu
                open={selectCompanyOpen}
                anchorEl={anchorEl}
                onClose={() => setSelectCompanyOpen(false)}
              >
                <div style={{ padding: "8px 16px" }}>
                  <TextField
                    autoFocus
                    fullWidth
                    placeholder="Search companies..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {filteredCompanies.map((company) => (
                  <MenuItem
                    key={company.id}
                    onClick={() => {
                      saveActiveCompanyId(company.id);
                      setSelectCompanyOpen(false);
                    }}
                    disabled={company.id === activeCompanyId}
                    selected={company.id === activeCompanyId}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <ListItemAvatar>
                      <Avatar src={company.logoUrl}>
                        {toAvatarName(company.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{
                        "& .MuiListItemText-primary": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {company.name}
                    </ListItemText>
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleClose("companies");
                    setSelectCompanyOpen(false);
                  }}
                >
                  <ListItemIcon>
                    <Pencil />
                  </ListItemIcon>
                  <ListItemText>Edit Companies</ListItemText>
                </MenuItem>
              </Menu>
            </ListItem>
            {!isFeatureHidden(FEATURES.DASHBOARD) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/dashboard")
                }
                selected={subScreenName === "dashboard"}
              >
                <ListItemIcon>
                  <BarChart3 />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.CHAT) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/chats")
                }
                selected={subScreenName === "chats"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.CHAT) ? <Lock /> : <MessageCircle />}
                </ListItemIcon>
                <ListItemText primary="Chat" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.EMPLOYEE) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/employees")
                }
                disabled={isFeatureLocked(FEATURES.EMPLOYEE)}
                selected={subScreenName === "employees"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.EMPLOYEE) ? <Lock /> : <Users />}
                </ListItemIcon>
                <ListItemText primary={employeesText} />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.KNOWLEDGE_BASE) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen(
                    "companies/" + activeCompanyId + "/knowledge-base"
                  )
                }
                selected={subScreenName === "knowledge-base"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.KNOWLEDGE_BASE) ? (
                    <Lock />
                  ) : (
                    <Lightbulb />
                  )}
                </ListItemIcon>
                <ListItemText primary="Knowledge Base" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.WORKFLOW) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/workflows")
                }
                disabled={isFeatureLocked(FEATURES.WORKFLOW)}
                selected={subScreenName === "workflows"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.WORKFLOW) ? (
                    <Lock />
                  ) : (
                    <Network />
                  )}
                </ListItemIcon>
                <ListItemText primary="Workflows" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.SMART_LIST) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/lists")
                }
                disabled={isFeatureLocked(FEATURES.SMART_LIST)}
                selected={subScreenName === "lists"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.SMART_LIST) ? (
                    <Lock />
                  ) : (
                    <ListChecks />
                  )}
                </ListItemIcon>
                <ListItemText primary="Smart Lists" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.SEQUENCE) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/sequences")
                }
                disabled={isFeatureLocked(FEATURES.SEQUENCE)}
                selected={subScreenName === "sequences"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.SEQUENCE) ? <Lock /> : <Route />}
                </ListItemIcon>
                <ListItemText primary="Sequences" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.INBOX) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/inbox")
                }
                disabled={isFeatureLocked(FEATURES.INBOX)}
                selected={subScreenName === "inbox"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.INBOX) ? <Lock /> : <Inbox />}
                </ListItemIcon>
                <ListItemText primary="Inbox" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.CONTENT_ENGINE) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen(
                    "companies/" + activeCompanyId + "/content-engine"
                  )
                }
                disabled={isFeatureLocked(FEATURES.CONTENT_ENGINE)}
                selected={subScreenName === "content-engine"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.CONTENT_ENGINE) ? (
                    <Lock />
                  ) : (
                    <Megaphone />
                  )}
                </ListItemIcon>
                <ListItemText primary="Content Engine" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.AGENT) &&
              isFeatureEnabled(FEATURES.AGENT) && (
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() =>
                    changeScreen("companies/" + activeCompanyId + "/agents")
                  }
                  selected={subScreenName === "agents"}
                >
                  <ListItemIcon>
                    <Bot size={24} />
                  </ListItemIcon>
                  <ListItemText primary="Agents" />
                </ListItemButton>
              )}
            {!isFeatureHidden(FEATURES.COLLABORATORS) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen(
                    "companies/" + activeCompanyId + "/collaborators"
                  )
                }
                disabled={isFeatureLocked(FEATURES.COLLABORATORS)}
                selected={subScreenName === "collaborators"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.COLLABORATORS) ? (
                    <Lock />
                  ) : (
                    <UserPlus />
                  )}
                </ListItemIcon>
                <ListItemText primary="Collaborators" />
              </ListItemButton>
            )}
            {!isFeatureHidden(FEATURES.INTEGRATIONS) && (
              <ListItemButton
                sx={{ pl: 4 }}
                onClick={() =>
                  changeScreen("companies/" + activeCompanyId + "/integrations")
                }
                disabled={isFeatureLocked(FEATURES.INTEGRATIONS)}
                selected={subScreenName === "integrations"}
              >
                <ListItemIcon>
                  {isFeatureLocked(FEATURES.INTEGRATIONS) ? (
                    <Lock />
                  ) : (
                    <Blocks size={24} />
                  )}
                </ListItemIcon>
                <ListItemText primary="Integrations" />
              </ListItemButton>
            )}
            {(menuApps.length > 0
              ? menuApps
              : ((whitelabelSettings?.apps || []) as Array<{
                  id: string;
                  name: string;
                  icon: string;
                  url?: string;
                  enabled?: boolean;
                }>)
            )
              .filter((app) => app.enabled !== false)
              .map((app) => (
                <ListItemButton
                  key={app.id}
                  sx={{ pl: 4 }}
                  onClick={() =>
                    changeScreen(
                      "companies/" + activeCompanyId + "/app/" + app.id
                    )
                  }
                  selected={subScreenName === "app" && instanceId === app.id}
                >
                  <ListItemIcon>
                    <LucideIcon name={app.icon} size={24} />
                  </ListItemIcon>
                  <ListItemText primary={app.name} />
                </ListItemButton>
              ))}
            {/* Only show Content Tools dropdown if at least one tool is visible */}
            {(!isFeatureHidden(FEATURES.IMAGE) ||
              !isFeatureHidden(FEATURES.AUDIO) ||
              !isFeatureHidden(FEATURES.VIDEO) ||
              !isFeatureHidden(FEATURES.LANDING_PAGES) ||
              !isFeatureHidden(FEATURES.KEYWORD_MONITORING) ||
              !isFeatureHidden(FEATURES.MARKETING_CAMPAIGN_EMAIL)) && (
              <>
                <ListItemButton sx={{ pl: 4 }} onClick={handleClick}>
                  <ListItemIcon>
                    <FlaskConical />
                  </ListItemIcon>
                  <ListItemText primary="Content Tools" />
                  {toolsOpen ? <ChevronUp /> : <ChevronDown />}
                </ListItemButton>
                <Collapse in={toolsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {!isFeatureHidden(FEATURES.IMAGE) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/images"
                          )
                        }
                        disabled={isFeatureLocked(FEATURES.IMAGE)}
                        selected={subScreenName == "images"}
                      >
                        <ListItemIcon>
                          {isFeatureLocked(FEATURES.IMAGE) ? (
                            <Lock />
                          ) : (
                            <ImageIcon />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Images" />
                      </ListItemButton>
                    )}
                    {!isFeatureHidden(FEATURES.AUDIO) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/audio"
                          )
                        }
                        disabled={isFeatureLocked(FEATURES.AUDIO)}
                        selected={subScreenName == "audio"}
                      >
                        <ListItemIcon>
                          {isFeatureLocked(FEATURES.AUDIO) ? (
                            <Lock />
                          ) : (
                            <Music />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Audio" />
                      </ListItemButton>
                    )}
                    {!isFeatureHidden(FEATURES.VIDEO) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/video"
                          )
                        }
                        disabled={isFeatureLocked(FEATURES.VIDEO)}
                        selected={subScreenName == "video"}
                      >
                        <ListItemIcon>
                          {isFeatureLocked(FEATURES.VIDEO) ? (
                            <Lock />
                          ) : (
                            <Video />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Video" />
                      </ListItemButton>
                    )}
                    {!isFeatureHidden(FEATURES.AUDIO) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/voices"
                          )
                        }
                        disabled={isFeatureLocked(FEATURES.AUDIO)}
                        selected={subScreenName == "voices"}
                      >
                        <ListItemIcon>
                          {isFeatureLocked(FEATURES.AUDIO) ? (
                            <Lock />
                          ) : (
                            <Mic />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Voices" />
                      </ListItemButton>
                    )}
                    {!isFeatureHidden(FEATURES.LANDING_PAGES) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/landing-pages"
                          )
                        }
                        disabled={isFeatureLocked(FEATURES.LANDING_PAGES)}
                        selected={subScreenName == "landing-pages"}
                      >
                        <ListItemIcon>
                          {isFeatureLocked(FEATURES.LANDING_PAGES) ? (
                            <Lock />
                          ) : (
                            <Globe />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary="Landing Pages"
                          secondary={
                            <Chip label="Beta" size="small" sx={{ height: 16, fontSize: "0.6rem", mt: 0.5 }} />
                          }
                        />
                      </ListItemButton>
                    )}
                    {!isFeatureHidden(FEATURES.KEYWORD_MONITORING) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/monitoring"
                          )
                        }
                        disabled={isFeatureLocked(FEATURES.KEYWORD_MONITORING)}
                        selected={subScreenName == "monitoring"}
                      >
                        <ListItemIcon>
                          {isFeatureLocked(FEATURES.KEYWORD_MONITORING) ? (
                            <Lock />
                          ) : (
                            <Target />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary="Keyword Monitoring"
                          secondary={
                            <Chip label="Beta" size="small" sx={{ height: 16, fontSize: "0.6rem", mt: 0.5 }} />
                          }
                        />
                      </ListItemButton>
                    )}
                    {!isFeatureHidden(FEATURES.MARKETING_CAMPAIGN_EMAIL) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/marketing-campaigns"
                          )
                        }
                        disabled={isFeatureLocked(FEATURES.MARKETING_CAMPAIGN_EMAIL)}
                        selected={subScreenName == "marketing-campaigns"}
                      >
                        <ListItemIcon>
                          {isFeatureLocked(FEATURES.MARKETING_CAMPAIGN_EMAIL) ? (
                            <Lock />
                          ) : (
                            <Megaphone />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary="Marketing Campaigns"
                          secondary={
                            <Chip label="Beta" size="small" sx={{ height: 16, fontSize: "0.6rem", mt: 0.5 }} />
                          }
                        />
                      </ListItemButton>
                    )}
                    {!isFeatureHidden(FEATURES.APP_BUILDER) && (
                      <ListItemButton
                        sx={{ pl: 6 }}
                        onClick={() =>
                          changeScreen(
                            "companies/" + activeCompanyId + "/app-builder"
                          )
                        }
                        selected={subScreenName == "app-builder"}
                      >
                        <ListItemIcon>
                          <Layers />
                        </ListItemIcon>
                        <ListItemText
                          primary="App Builder"
                          secondary={
                            <Chip label="Beta" size="small" sx={{ height: 16, fontSize: "0.6rem", mt: 0.5 }} />
                          }
                        />
                      </ListItemButton>
                    )}
                  </List>
                </Collapse>
              </>
            )}
            <Divider />
            {resumableOnboardingCompany && (
              <OnboardingProgressMenuItem
                company={resumableOnboardingCompany}
                onClick={() =>
                  navigate("/onboarding", {
                    state: {
                      resumeCompanyId: resumableOnboardingCompany.id,
                      fromCompanies: true,
                    },
                  })
                }
              />
            )}
            <ListItemButton
              onClick={() => changeScreen("profile")}
              selected={screenName === "profile"}
            >
              <ListItemIcon>
                <UserCircle />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                const supportUrl = getSupportContactInfo(whitelabelSettings);
                if (supportUrl) {
                  window.open(supportUrl, "_blank");
                } else {
                  changeScreen("your-tickets");
                }
              }}
              selected={screenName === "your-tickets"}
            >
              <ListItemIcon>
                <Headset />
              </ListItemIcon>
              <ListItemText primary="Support" />
            </ListItemButton>
            {(usage.whitelabelLimit > 0 || hasWhitelabelAccess) && (
              <ListItemButton
                onClick={() => changeScreen("whitelabel")}
                selected={screenName === "whitelabel"}
              >
                <ListItemIcon>
                  <BadgeCheck />
                </ListItemIcon>
                <ListItemText primary="White-Label" />
              </ListItemButton>
            )}
            {isSuperUser && (
              <ListItemButton
                onClick={() => changeScreen("admin")}
                selected={screenName === "admin"}
              >
                <ListItemIcon>
                  <ShieldCheck />
                </ListItemIcon>
                <ListItemText primary="Admin" />
              </ListItemButton>
            )}

            {isFreeUser(currentPlan) &&
              !isOrgAccount &&
              usageLoaded &&
              activeCompanyId &&
              planLoaded &&
              (whitelabelSettings?.customSubscriptionEnabled ? (
                <ListItemButton
                  onClick={() => {
                    const salesEmail =
                      whitelabelSettings?.salesEmail ||
                      getSupportContactInfo(whitelabelSettings);
                    console.log(
                      "[Dashboard Mobile] Contact Sales clicked - whitelabelSettings.salesEmail:",
                      whitelabelSettings?.salesEmail
                    );
                    console.log(
                      "[Dashboard Mobile] Contact Sales clicked - final salesEmail:",
                      salesEmail
                    );
                    window.open(
                      `mailto:${salesEmail}?subject=Subscription Inquiry`,
                      "_blank"
                    );
                  }}
                >
                  <ListItemIcon>
                    <Zap />
                  </ListItemIcon>
                  <ListItemText primary="Contact Sales" />
                </ListItemButton>
              ) : (
                <ListItemButton onClick={() => {
                  setDrawerOpen(false);
                  setSubscriptionDialogOpen(true);
                }}>
                  <ListItemIcon>
                    <Zap />
                  </ListItemIcon>
                  <ListItemText primary="Upgrade" />
                </ListItemButton>
              ))}

            <ListItemButton
              onClick={() => {
                if (whitelabelSettings?.documentationUrl) {
                  window.open(whitelabelSettings.documentationUrl, "_blank");
                } else {
                  changeScreen("documentation");
                }
              }}
              selected={screenName === "documentation"}
            >
              <ListItemIcon>
                <BookOpen />
              </ListItemIcon>
              <ListItemText primary="Documentation" />
            </ListItemButton>

            {/* Mobile-only menu items */}
            {isMobile && (
              <>
                <Divider sx={{ my: 1 }} />
                {!isFeatureHidden(FEATURES.PERSONAL_ASSISTANT) && (
                  <ListItemButton
                    onClick={() => {
                      setPersonalAssistantOpen(true);
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      {personalAssistantPrefs?.personaImageUrl ? (
                        <Avatar
                          src={personalAssistantPrefs.personaImageUrl}
                          sx={{ width: 24, height: 24 }}
                        />
                      ) : (
                        <Bot size={24} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {personalAssistantPrefs?.personaName || PERSONAL_ASSISTANT_NAME}
                          <Chip label="Beta" size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                        </Box>
                      }
                      secondary={personalAssistantPrefs?.personaName ? PERSONAL_ASSISTANT_NAME : undefined}
                    />
                  </ListItemButton>
                )}
                <ListItemButton
                  onClick={() =>
                    setThemeMode(mode === "light" ? "dark" : "light")
                  }
                >
                  <ListItemIcon>
                    {mode === "dark" ? <Sun /> : <Moon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={mode === "dark" ? "Light Mode" : "Dark Mode"}
                  />
                </ListItemButton>
              </>
            )}

            <ListItemButton onClick={signOutAction}>
              <ListItemIcon>
                <LogOut />
              </ListItemIcon>
              <ListItemText primary="Sign Out" />
            </ListItemButton>
          </List>
        </Drawer>
        <Main
          open={drawerOpen}
          isMobile={isMobile}
          sx={{
            pt: subScreenName === "chats" || subScreenName === "app" ? 0 : 2,
            minHeight: "100vh",
            boxSizing: "border-box",
            marginTop: "0px",
          }}
        >
          <DrawerHeader theme={theme} />
          <DashboardTutorialRunner screenName={screenName || ""} companyId={companyId} />
          {screenName === "companies" && !companyId && (
            <Companies
              currentUser={currentUser}
              token={token}
              getToken={getToken}
              eventListener={eventListener}
              openDialog={openDialog}
              onNewOrganization={(company: any) => getCompanies()}
            />
          )}
          {screenName === "profile" && (
            <Profile
              currentUser={currentUser}
              token={token}
              getToken={getToken}
              eventListener={eventListener}
              mode={mode}
            />
          )}
          {screenName === "whitelabel" && !companyId && (
            <>
              <WhiteLabel
                currentUser={currentUser}
                getToken={getToken}
                mode={mode}
                eventListener={eventListener}
              />
            </>
          )}
          {screenName === "admin" && !companyId && !instanceId && (
            <AdminPage
              getToken={getToken}
              currentCompanyId={activeCompanyId || ""}
              initialSection={subScreenName}
              isSuperUser={isSuperUser}
            />
          )}
          {screenName === "support" &&
            !companyId &&
            !instanceId &&
            !subScreenName && (
              <Support
                companyId={activeCompanyId || ""}
                getToken={getToken}
                setError={(error: string) =>
                  eventListener({ type: "SET_ERROR", error })
                }
                setSuccess={(message: string) =>
                  eventListener({ type: "SET_SUCCESS", message })
                }
                companies={companies}
                showConfirmDialog={openDialog}
                isSuperUser={isSuperUser}
              />
            )}
          {screenName === "support" &&
            subScreenName &&
            !companyId &&
            !instanceId && (
              <SupportTicketDetail
                getToken={getToken}
                setError={(error: string) =>
                  eventListener({ type: "SET_ERROR", error })
                }
                setSuccess={(message: string) =>
                  eventListener({ type: "SET_SUCCESS", message })
                }
                ticketId={subScreenName}
                isSuperUser={isSuperUser}
              />
            )}
          {screenName === "documentation" && !companyId && (
            <Documentation getToken={getToken} isSuperUser={isSuperUser} eventListener={eventListener} />
          )}
          {screenName === "your-tickets" &&
            !companyId &&
            !instanceId &&
            !subScreenName && (
              <Support
                getToken={getToken}
                setError={(error: string) =>
                  eventListener({ type: "SET_ERROR", error })
                }
                setSuccess={(message: string) =>
                  eventListener({ type: "SET_SUCCESS", message })
                }
                companies={companies}
                showConfirmDialog={openDialog}
                isSuperUser={isSuperUser}
              />
            )}
          {screenName === "company" && activeCompanyId && (
            <Company
              currentCompanyPlan={currentPlan}
              company={company}
              employees={employees}
              mode={mode}
              currentUser={currentUser}
              id={activeCompanyId}
              token={token}
              getToken={getToken}
              eventListener={eventListener}
              openDialog={openDialog}
              subScreenName={subScreenName}
              instanceId={instanceId}
              refreshEmployees={getEmployees}
              isSuperUser={isSuperUser}
              instanceConfig={instanceConfig}
              usage={usage}
              companyRole={companyRole}
              menuApps={menuApps}
            />
          )}
          {screenName === "companies" && companyId && (
            <Company
              currentCompanyPlan={currentPlan}
              company={company}
              employees={employees}
              mode={mode}
              currentUser={currentUser}
              id={activeCompanyId || ""}
              token={token}
              getToken={getToken}
              eventListener={eventListener}
              openDialog={openDialog}
              subScreenName={subScreenName}
              instanceId={instanceId}
              refreshEmployees={getEmployees}
              isSuperUser={isSuperUser}
              instanceConfig={instanceConfig}
              usage={usage}
              companyRole={companyRole}
              menuApps={menuApps}
            />
          )}
          {screenName === "integrations" && activeCompanyId && (
            <Company
              currentCompanyPlan={currentPlan}
              company={company}
              employees={employees}
              mode={mode}
              currentUser={currentUser}
              id={activeCompanyId || ""}
              token={token}
              getToken={getToken}
              eventListener={eventListener}
              openDialog={openDialog}
              subScreenName={"integrations"}
              instanceId={instanceId}
              refreshEmployees={getEmployees}
              isSuperUser={isSuperUser}
              instanceConfig={instanceConfig}
              usage={usage}
              companyRole={companyRole}
              menuApps={menuApps}
            />
          )}
        </Main>

        {/* Support Ticket Dialog */}
        <TicketFormDialog
          open={ticketDialogOpen}
          onClose={() => setTicketDialogOpen(false)}
          companies={companies}
          getToken={getToken}
          onSave={() => {
            eventListener({
              type: "SET_SUCCESS",
              message: "Support ticket created successfully",
            });
          }}
          onError={(error: string) =>
            eventListener({ type: "SET_ERROR", error })
          }
        />

        {/* Subscription Dialog */}
        {subscriptionDialogOpen && (
          <SubscriptionDialog
            open={subscriptionDialogOpen}
            onClose={() => setSubscriptionDialogOpen(false)}
            currentUser={currentUser}
            getUsage={getCurrentPlan}
            getToken={getToken}
            eventListener={eventListener}
            mode={mode}
            currentPlan={userCurrentPlan}
            hasExistingSubscription={!isFreeUser(userCurrentPlan)}
            currentSubscriptionPlan={userCurrentPlan}
            currentSubscriptionConfig={{
              seats: usage?.seats || 1,
              companies: usage?.companyLimit || 1,
              creditPackages: usage?.creditPackages || 0,
            }}
            onSubscriptionUpdate={() => {
              getCurrentPlan();
            }}
          />
        )}

        {/* User Impersonation Dialog */}
        <UserImpersonationDialog
          open={impersonationDialogOpen}
          onClose={() => setImpersonationDialogOpen(false)}
          getToken={getToken}
          onImpersonate={handleImpersonate}
        />

        {/* Demo Settings Dialog */}
        <DemoSettingsDialog
          open={demoSettingsDialogOpen}
          onClose={() => setDemoSettingsDialogOpen(false)}
          hideLogo={hideLogo}
          demoMode={demoMode}
          onHideLogoChange={setHideLogo}
          onDemoModeChange={setDemoMode}
        />

        {/* Executive Assistant - floating chat widget available on all screens */}
        {/* On mobile, opened from drawer menu; on desktop, shows floating bar */}
        {!isFeatureHidden(FEATURES.PERSONAL_ASSISTANT) && (
          <PersonalAssistant
            getToken={getToken}
            companyId={activeCompanyId}
            externalOpen={personalAssistantOpen}
            onOpenChange={setPersonalAssistantOpen}
            onPreferencesLoaded={setPersonalAssistantPrefs}
          />
        )}
      </Box>
      </NotificationProvider>
    </TutorialProvider>
  );
};

// Component to initialize tutorial state from usage data
// This needs to be inside TutorialProvider to access useTutorial
const DashboardTutorialInitializer: React.FC<{
  usage: any;
  isNewUser: boolean;
  getToken: () => Promise<string>;
}> = ({ usage, isNewUser, getToken }) => {
  const { setTutorialProgress, setIsNewUser } = useTutorial();

  useEffect(() => {
    // Set whether user is new (for showing tutorials)
    setIsNewUser(isNewUser);
  }, [isNewUser, setIsNewUser]);

  useEffect(() => {
    // Load tutorial progress from usage data
    // tutorialProgress is included in the usage response (from User model)
    // so no need for a separate API call — treat null as empty
    if (usage && usage.id) {
      setTutorialProgress(usage.tutorialProgress || {});
    }
  }, [usage, setTutorialProgress]);

  return null;
};

// Component to show Executive Assistant tutorial on first dashboard load
const ExecutiveAssistantTutorialInitializer: React.FC<{ usageLoaded: boolean }> = ({ usageLoaded }) => {
  const [showTutorial, setShowTutorial] = useState(false);
  const { hasTutorialCompleted, markTutorialComplete, tutorialEnabled } = useTutorial();
  const hasShownRef = React.useRef(false);

  useEffect(() => {
    // Wait until usage is loaded (which populates tutorialProgress)
    // Don't show if already shown this session or if tutorials are disabled
    if (!usageLoaded || hasShownRef.current || !tutorialEnabled) {
      return;
    }

    // Check if tutorial should be shown
    // Small delay to let the dashboard render first
    const timer = setTimeout(() => {
      if (!hasTutorialCompleted(TUTORIAL_PAGES.EXECUTIVE_ASSISTANT)) {
        hasShownRef.current = true;
        setShowTutorial(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [usageLoaded, hasTutorialCompleted, tutorialEnabled]);

  const handleClose = () => {
    markTutorialComplete(TUTORIAL_PAGES.EXECUTIVE_ASSISTANT);
    setShowTutorial(false);
  };

  return (
    <ExecutiveAssistantTutorialDialog
      open={showTutorial}
      onClose={handleClose}
    />
  );
};

// Map non-company screens to tutorial config
const dashboardTutorialMapping: Record<string, { pageKey: TutorialPageKey; steps: Step[] }> = {
  profile: { pageKey: TUTORIAL_PAGES.PROFILE, steps: profileTutorialSteps },
  companies: { pageKey: TUTORIAL_PAGES.COMPANIES, steps: companiesTutorialSteps },
};

// Component to run tutorial for non-company dashboard pages
const DashboardTutorialRunner: React.FC<{ screenName: string; companyId?: string }> = ({ screenName, companyId }) => {
  // Only run for non-company pages (profile, companies without companyId)
  const shouldRun = screenName === "profile" || (screenName === "companies" && !companyId);
  const config = shouldRun ? dashboardTutorialMapping[screenName] : null;

  const { runTutorial, steps, handleJoyrideCallback } = useTutorialPage({
    pageKey: config?.pageKey || TUTORIAL_PAGES.PROFILE,
    steps: config?.steps || [],
  });

  if (!config) {
    return null;
  }

  return (
    <TutorialWrapper
      run={runTutorial}
      steps={steps}
      callback={handleJoyrideCallback}
    />
  );
};

export default Dashboard;
